/**
 * Pure SDK → chart mapping for `ConnectedChartContainer`.
 *
 * The Morfi backend returns money/probability/price fields as JSON strings
 * with three different scales — D6 (USDC microunits), D9 (price-per-share
 * and PPB probability), and plain decimal (already in display form). The
 * TypeScript type system can't distinguish them at the boundary; everything
 * comes off the SDK as `string`. Wiring the wrong converter to a field is
 * a silent bug that shows up as 10⁹-off values on the chart — exactly what
 * happened with `outcomes[].price` (was `toNum`, should be `d9ToNum`,
 * producing a y-axis of "$10,838,208.00" instead of "$0.0108").
 *
 * Extracting these mappers out of the React component (a) keeps the
 * scale-converter choice in one place so the answer to "is this field
 * D6 or D9?" is local and reviewable, and (b) lets us assert against
 * realistic SDK fixtures in a unit test without spinning up an SDK
 * provider.
 *
 * Tests live at `frontend/tests/unit/lib/chart/sdk-mapping.test.ts`.
 */

import { convictionFraction, type GetTradesRow } from '@morfi/backend-sdk-v2/client';
import { outcomeMarketCapAtPriceD6 } from '@morfi/market-math';
import type { MarketChartOutcome } from '@/components/chart/market-chart';
import { D6_SCALE, D9_SCALE } from '@/config/solana';
import { resolveTradeSide } from '@/lib/chart/trade-side';
import type { TradeAnnotation, UTCTimestamp } from '@/lib/chart/types';

// ─── Scale converters ───────────────────────────────────────────────────────

/** D6 USDC string → number (one million microunits → 1.0). */
export function d6ToNum(value: string | null | undefined, fallback: number = 0): number {
  if (value === null || value === undefined) return fallback;
  const n = Number(value);
  return Number.isNaN(n) ? fallback : n / D6_SCALE;
}

/** D9 string → number (one billion microunits → 1.0). Used for prices and PPB. */
export function d9ToNum(value: string | null | undefined, fallback: number = 0): number {
  if (value === null || value === undefined) return fallback;
  const n = Number(value);
  return Number.isNaN(n) ? fallback : n / D9_SCALE;
}

// ─── Property → converter (used by ChartLeaf for historical buckets) ───────

/**
 * Pick the converter to apply to a historical chart-bucket value based on
 * which `property` the chart is plotting:
 *  - `twar` is conviction PPB → the SDK's `convictionFraction` (the single
 *    source of truth; raw 0→~2.0, never normalised — matches the live
 *    `o.twar = convictionFraction(o.conviction)` and the chart legend).
 *  - `price` is `price_d9` → `d9ToNum`.
 *  - everything else (volume, reserves, market cap) is USDC D6 → `d6ToNum`.
 */
export function pickChartLeafConverter(
  property: string,
): (value: string | null | undefined) => number {
  if (property === 'twar') return convictionFraction;
  if (property === 'price') return d9ToNum;
  return d6ToNum;
}

// ─── Outcome mapping ───────────────────────────────────────────────────────

/**
 * Minimal structural shape we read off the SDK outcome row. Kept local
 * (not the full SDK type) so this mapper stays testable without an SDK
 * provider — the test can pass plain object literals.
 */
export interface SdkOutcomeShape {
  readonly outcomeIndex: number;
  readonly label: string;
  // Optional to match the SDK-generated shape (the field is omitted when not
  // requested via `fields`). Falls back to 0 via `d9ToNum`'s default.
  readonly conviction?: string;
  readonly price: string;
  readonly reserves?: string;
  readonly effectiveMarketCapD6: string;
}

/**
 * Map a single SDK outcome row to the `MarketChartOutcome` shape that
 * `<MarketChart>` consumes. `displayTitle` is supplied by the caller so the
 * on-chain name override (which depends on RPC state) stays out of this
 * pure function.
 */
export function mapOutcomeToChartOutcome(
  o: SdkOutcomeShape,
  displayTitle: string,
): MarketChartOutcome {
  return {
    index: Number(o.outcomeIndex),
    title: displayTitle,
    // Raw per-outcome conviction (0→~2.0) via the SDK's single source of truth.
    twar: convictionFraction(o.conviction),
    // `price` is `postOutcomePriceD9` per the SDK manifest — D9-scaled
    // USDC-per-share. Previously routed through `toNum`, which left the raw
    // D9 integer in place and rendered as multi-million-dollar share prices.
    price: d9ToNum(o.price),
    marketCap: d6ToNum(o.effectiveMarketCapD6),
  };
}

// ─── Current CP market cap ─────────────────────────────────────────────────

export function currentCpMarketCapD6(input: {
  readonly priceD9: string | null | undefined;
  readonly indexedOutcomeMintSupplyD6: string | null | undefined;
}): bigint | undefined {
  const priceD9 = unsignedInteger(input.priceD9);
  const indexedOutcomeMintSupplyD6 = unsignedInteger(input.indexedOutcomeMintSupplyD6);
  if (priceD9 === null || indexedOutcomeMintSupplyD6 === null) return undefined;
  const result = outcomeMarketCapAtPriceD6({
    poolModel: 'cp_swap',
    priceD9,
    indexedOutcomeMintSupplyD6,
  });
  return result.available && result.value > BigInt(0) ? result.value : undefined;
}

export type TradeLiquidityModel = 'bonding_curve' | 'cp_swap';

export function parseTradeLiquidityModel(
  value: string | null | undefined,
): TradeLiquidityModel | undefined {
  return value === 'bonding_curve' || value === 'cp_swap' ? value : undefined;
}

export function currentOutcomeSpotChartValue(
  priceD9: string | null | undefined,
): number | undefined {
  const price = unsignedInteger(priceD9);
  if (price === null || price <= BigInt(0)) return undefined;
  const value = d9ToNum(price.toString());
  return Number.isFinite(value) && value > 0 ? value : undefined;
}

/**
 * Render one venue-proven trade-tape value. Price history accepts both pools;
 * CP market cap is execution price times indexed supply, while BC market cap
 * applies the execution price to the stable curve constants.
 */
export function tradeTapeChartValue(input: {
  readonly liquidityModel: TradeLiquidityModel | null | undefined;
  readonly priceD9: string | null | undefined;
  readonly mode: 'price' | 'marketCap';
  readonly indexedOutcomeMintSupplyD6?: string | null | undefined;
  readonly initialOutcomeSupplyD6?: string | null | undefined;
  readonly virtualQuoteD6?: string | null | undefined;
}): number | undefined {
  const liquidityModel = parseTradeLiquidityModel(input.liquidityModel);
  const priceD9 = unsignedInteger(input.priceD9);
  if (liquidityModel === undefined || priceD9 === null || priceD9 <= BigInt(0)) return undefined;

  if (input.mode === 'price') return currentOutcomeSpotChartValue(priceD9.toString());
  if (liquidityModel === 'bonding_curve') {
    const initialOutcomeSupplyD6 = unsignedInteger(input.initialOutcomeSupplyD6);
    const virtualQuoteReserveD6 = unsignedInteger(input.virtualQuoteD6);
    if (initialOutcomeSupplyD6 === null || virtualQuoteReserveD6 === null) return undefined;
    const marketCap = outcomeMarketCapAtPriceD6({
      poolModel: 'bonding_curve',
      priceD9,
      initialOutcomeSupplyD6,
      virtualQuoteReserveD6,
    });
    if (!marketCap.available || marketCap.value <= BigInt(0)) return undefined;
    return d6ToNum(marketCap.value.toString());
  }

  const marketCapD6 = currentCpMarketCapD6({
    priceD9: priceD9.toString(),
    indexedOutcomeMintSupplyD6: input.indexedOutcomeMintSupplyD6,
  });
  if (marketCapD6 === undefined) return undefined;
  const value = d6ToNum(marketCapD6.toString());
  return Number.isFinite(value) && value > 0 ? value : undefined;
}

// ─── Break-even market cap ──────────────────────────────────────────────────

/**
 * Stable curve constants for the break-even market-cap mapping.
 *
 * Critically, NONE of these are realtime-patched: the `useGetMarket` recipe
 * patches an outcome's `price`, `effectiveMarketCapD6`, and `conviction` on
 * every trade while preserving the virtual-only `reserves` alias. Deriving a
 * curve constant from those changing marks would make the line wander on other
 * traders' activity. Instead we read the genuine constants: the graduated
 * circulating supply (`totalSupply`, only moves on burns) or the bonding-curve
 * cap + virtual quote straight from the BondingCurve account.
 */
export interface BreakEvenMcapInputs {
  /** True once the outcome has graduated to the cp-swap AMM. */
  readonly graduated: boolean;
  /** Indexed migrated-winner outcome mint supply (D6) — graduated path only. */
  readonly indexedOutcomeMintSupplyD6?: string | null | undefined;
  /** Lifetime curve token cap `S₀` (D6) from chain — bonding-curve path only. */
  readonly initialOutcomeSupplyD6?: string | null | undefined;
  /** Virtual quote reserve `V` (D6) from chain — bonding-curve path only. */
  readonly virtualQuoteD6?: string | null | undefined;
}

/**
 * Break-even market cap (D6) for a given D9 break-even price.
 *
 * The relation between price and market cap is exact in each regime:
 *   - graduated AMM token: `mcap = price · supply` (proportional, no offset).
 *   - bonding curve (constant-product): `mcap = price · S₀ − V` (affine), where
 *     `S₀` is the lifetime token cap and `V` the virtual quote reserve.
 * Both forms use STABLE curve constants, so the line stays put while the market
 * trades — it only moves when the break-even PRICE itself moves (fees/slippage).
 *
 * Returns `undefined` when a constant is missing (e.g. on-chain reserves not
 * loaded yet) or the result is non-positive, so the caller can omit the
 * market-cap line while still drawing the price one.
 */
export function breakEvenMarketCapD6(
  breakEvenPriceD9: bigint,
  inputs: BreakEvenMcapInputs,
): bigint | undefined {
  if (inputs.graduated) {
    return currentCpMarketCapD6({
      priceD9: breakEvenPriceD9.toString(),
      indexedOutcomeMintSupplyD6: inputs.indexedOutcomeMintSupplyD6,
    });
  }

  const initialOutcomeSupplyD6 = unsignedInteger(inputs.initialOutcomeSupplyD6);
  const virtualQuoteReserveD6 = unsignedInteger(inputs.virtualQuoteD6);
  if (initialOutcomeSupplyD6 === null || virtualQuoteReserveD6 === null) return undefined;
  const result = outcomeMarketCapAtPriceD6({
    poolModel: 'bonding_curve',
    priceD9: breakEvenPriceD9,
    initialOutcomeSupplyD6,
    virtualQuoteReserveD6,
  });
  return result.available && result.value > BigInt(0) ? result.value : undefined;
}

function unsignedInteger(value: string | null | undefined): bigint | null {
  return typeof value === 'string' && /^\d+$/.test(value) ? BigInt(value) : null;
}

// ─── Trade mapping ─────────────────────────────────────────────────────────

export interface SdkTradeShape {
  readonly eventId?: string;
  readonly timestamp?: string;
  readonly priceAfter?: string;
  readonly priceBefore?: string;
  readonly type?: GetTradesRow['type'];
  readonly side?: GetTradesRow['side'];
  readonly liquidityModel?: 'bonding_curve' | 'cp_swap' | null;
  readonly usdcAmount?: string;
  readonly tokensAmount?: string;
  readonly outcomeIndex: number;
  readonly totalCost?: string;
  readonly signature?: string;
  readonly thesis?: string | null;
}

/** ISO-8601 string → UTC seconds, as the chart's `UTCTimestamp` brand. */
export function isoToTimestamp(iso: string): UTCTimestamp {
  return Math.floor(new Date(iso).getTime() / 1000) as UTCTimestamp;
}

function toTradeType(sdkType: GetTradesRow['type'] | undefined): TradeAnnotation['type'] {
  if (sdkType === 'sell') return 'sell';
  if (sdkType === 'swap') return 'swap';
  return 'buy';
}

/**
 * Map a single SDK trade row to the `TradeAnnotation` the chart consumes.
 * priceBefore/priceAfter are D9 strings (backend maps `row.price_d9` to
 * both fields — `backend-api-service/src/features/markets/schema.ts:742-743`),
 * so this routes them through `d9ToNum`.
 */
export function mapTradeToAnnotation(
  tr: SdkTradeShape,
  outcomeIndex: number = Number(tr.outcomeIndex),
): TradeAnnotation {
  const side = resolveTradeSide(tr);
  const base: TradeAnnotation = {
    timestamp: isoToTimestamp(tr.timestamp ?? ''),
    price: d9ToNum(String(tr.priceAfter ?? tr.priceBefore ?? '0')),
    type: toTradeType(tr.type),
    ...(side === undefined ? {} : { side }),
    usdcAmount: d6ToNum(String(tr.usdcAmount ?? '0')),
    tokensAmount: d6ToNum(String(tr.tokensAmount ?? '0')),
    outcomeIndex,
    totalCost: d6ToNum(String(tr.totalCost ?? '0')),
    signature: String(tr.eventId ?? tr.signature ?? ''),
  };
  if (tr.thesis !== null && tr.thesis !== undefined) base.thesis = tr.thesis;
  return base;
}
